import { RESERVATION_STATE } from "@/constants/common";
import useViewMode from "@/hooks/useViewMode";
import type { DailyReservationData } from "@/types/reservation";
import { convertTimeToHourMinuteText } from "@/utils/time";
import { cn } from "@/utils/cn";

type ReservationCardProps = {
  date: string;
  reservationList: DailyReservationData[];
  showHeader?: boolean;
  onItemClick?: (item: DailyReservationData) => void;
};

const statusColor: Record<string, string> = {
  approve: "bg-green-100 text-green-700",
  request: "bg-amber-50 text-amber-600",
  reject: "bg-red-50 text-red-600",
  cancel: "bg-gray-100 text-gray-500",
};

const ReservationCard = ({ date, reservationList, showHeader = true, onItemClick }: ReservationCardProps) => {
  const { isAdmin } = useViewMode();

  return (
    <div>
      {showHeader && (
        <div className="mb-3 text-sm font-medium text-gray-700">{date}</div>
      )}
      {reservationList.length > 0 ? (
        <div className="flex flex-col gap-2.5">
          {reservationList.map(item => {
            const {
              rsvtId,
              startTime,
              endTime,
              plcNm,
              useCnts,
              userName,
              telNo,
              sttCd,
            } = item;

            return (
              <div
                key={rsvtId}
                onClick={() => onItemClick?.(item)}
                className={cn(
                  "rounded-lg border border-gray-200 bg-white p-3.5 transition-all hover:border-gray-300",
                  onItemClick && "cursor-pointer hover:-translate-y-px hover:shadow-md",
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  {/* Left: time + details */}
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {convertTimeToHourMinuteText(startTime)} ~ {convertTimeToHourMinuteText(endTime)}
                      </span>
                      {sttCd && (
                        <span className={cn(
                          "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
                          statusColor[sttCd] || "bg-gray-100 text-gray-500",
                        )}>
                          {RESERVATION_STATE[sttCd]}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <span className="font-medium text-gray-700">{plcNm}</span>
                      <span className="text-gray-300">·</span>
                      <span className="truncate">{useCnts}</span>
                    </div>
                    {isAdmin && userName && (
                      <div className="text-xs text-gray-400">
                        {userName}{telNo ? ` · ${telNo}` : ""}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-gray-200 text-sm text-gray-400">
          예약 내역이 없습니다
        </div>
      )}
    </div>
  );
};

export default ReservationCard;
